import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { Field, inputCls, PrimaryButton } from "@/components/admin/parts";
import { Switch } from "@/components/ui/switch";
import { adminApi, getErrorMessage } from "@/lib/api";

function ToggleRow({ label, hint, checked, onChange, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-neutral-800/60">
      <div>
        <div className="text-sm text-white">{label}</div>
        <div className="text-xs text-neutral-500 mt-0.5">{hint}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}

export function ResearchSettingsCard() {
  const [cfg, setCfg] = useState(null);
  const [domains, setDomains] = useState("");
  const [saving, setSaving] = useState(false);

  const apply = (data) => { setCfg(data); setDomains((data.domains || []).join("\n")); };

  useEffect(() => {
    adminApi.get("/admin/research-settings").then(({ data }) => apply(data)).catch(() => toast.error("Couldn't load research settings"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await adminApi.put("/admin/research-settings", {
        enabled: cfg.enabled,
        approved_only: cfg.approved_only,
        domains: domains.split(/[\n,]+/).map((d) => d.trim()).filter(Boolean),
      });
      apply(data);
      toast.success("Research settings saved");
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-[#0F0F0F] border border-neutral-800 rounded-xl p-6" data-testid="research-settings-card">
      <div className="flex items-center gap-2 mb-1">
        <MagnifyingGlass size={20} className="text-sky-300" weight="duotone" />
        <h2 className="font-heading font-semibold text-white">Web Research (fallback)</h2>
      </div>
      <p className="text-sm text-neutral-500 mb-3">
        When the Knowledge Base and manuals don't cover a question, Nick can run a live web search. He always tells the
        customer it's a web-search result (not official OTM info), and the links are shown under his reply.
      </p>
      {!cfg ? <div className="text-neutral-600 text-sm">Loading...</div> : (
        <div>
          <ToggleRow label="Enable web research" hint="Off = Nick says he doesn't have it on file and points to the dealer." checked={cfg.enabled} onChange={(v) => setCfg({ ...cfg, enabled: v })} testId="research-enabled-toggle" />
          <ToggleRow label="Approved sources only" hint="On = only search the sites listed below. Off = search the whole web but prefer these sites." checked={cfg.approved_only} onChange={(v) => setCfg({ ...cfg, approved_only: v })} testId="research-approved-only-toggle" />
          <div className="pt-4">
            <Field label="Approved manufacturer sites" hint="One domain per line, e.g. truma.com">
              <textarea value={domains} onChange={(e) => setDomains(e.target.value)} rows={6} className={`${inputCls} font-mono text-xs`} data-testid="research-domains-input" />
            </Field>
          </div>
          <div className="flex justify-end pt-4">
            <PrimaryButton onClick={save} disabled={saving} data-testid="research-save-btn">{saving ? "Saving..." : "Save research settings"}</PrimaryButton>
          </div>
        </div>
      )}
    </div>
  );
}
